import React, { useEffect, useMemo, useState } from 'react';
import { getSettings, saveSettings } from '../utils/settings';
import { useApi } from '../context/ApiContext';

export default function SettingsAdmin() {
  const { adminGetWebsiteSettings, adminUpdateWebsiteSettings } = useApi();
  const initial = useMemo(() => getSettings(), []);
  const [allowApply, setAllowApply] = useState(initial.allowApplyFromProperties);
  const [requiredDocs, setRequiredDocs] = useState(initial.requiredDocuments || []);
  const [newDoc, setNewDoc] = useState('');
  const [manualEnabled, setManualEnabled] = useState(initial.manualCreationEnabled ?? true);

  const [backendSettings, setBackendSettings] = useState({
    leaseEmailSubject: '',
    leaseEmailBody: '',
    leaseEmailFromName: '',
    leaseEmailFromAddress: '',
    leaseEmailCc: '',
    leaseEmailBcc: '',
    paymentLink: '',
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankBranchCode: '',
    bankReferenceFormat: '',
    paymentEmailSubject: '',
    paymentEmailBody: '',
    paymentEmailFromName: '',
    paymentEmailFromAddress: '',
    paymentEmailCc: '',
    paymentEmailBcc: '',
  });
  const [leaseMergeFields, setLeaseMergeFields] = useState([]);
  const [paymentMergeFields, setPaymentMergeFields] = useState([]);
  const [loadingBackendSettings, setLoadingBackendSettings] = useState(true);
  const [savingBackendSettings, setSavingBackendSettings] = useState(false);
  const [backendError, setBackendError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingBackendSettings(true);
        setBackendError('');
        const data = await adminGetWebsiteSettings();
        if (!active) return;
        const leaseEmail = data?.leaseEmail || {};
        const paymentEmail = data?.paymentEmail || {};
        const bankDetails = data?.bankDetails || {};
        setBackendSettings({
          leaseEmailSubject: leaseEmail.subject || '',
          leaseEmailBody: leaseEmail.body || '',
          leaseEmailFromName: leaseEmail.fromName || '',
          leaseEmailFromAddress: leaseEmail.fromAddress || '',
          leaseEmailCc: leaseEmail.cc || '',
          leaseEmailBcc: leaseEmail.bcc || '',
          paymentLink: bankDetails.paymentLink || '',
          bankName: bankDetails.bankName || '',
          bankAccountName: bankDetails.accountName || '',
          bankAccountNumber: bankDetails.accountNumber || '',
          bankBranchCode: bankDetails.branchCode || '',
          bankReferenceFormat: bankDetails.referenceFormat || '',
          paymentEmailSubject: paymentEmail.subject || '',
          paymentEmailBody: paymentEmail.body || '',
          paymentEmailFromName: paymentEmail.fromName || '',
          paymentEmailFromAddress: paymentEmail.fromAddress || '',
          paymentEmailCc: paymentEmail.cc || '',
          paymentEmailBcc: paymentEmail.bcc || '',
        });
        setLeaseMergeFields(Array.isArray(leaseEmail.mergeFields) ? leaseEmail.mergeFields : []);
        setPaymentMergeFields(Array.isArray(paymentEmail.mergeFields) ? paymentEmail.mergeFields : []);
      } catch (e) {
        if (!active) return;
        setBackendError(e?.message || 'Failed to load email and payment settings.');
      } finally {
        if (active) setLoadingBackendSettings(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [adminGetWebsiteSettings]);

  function addDoc() {
    const value = newDoc.trim();
    if (!value) return;
    setRequiredDocs((list) => Array.from(new Set([...list, value])));
    setNewDoc('');
  }

  function removeDoc(name) {
    setRequiredDocs((list) => list.filter((x) => x !== name));
  }

  function saveWorkspaceSettings() {
    saveSettings({
      allowApplyFromProperties: allowApply,
      manualCreationEnabled: manualEnabled,
      requiredDocuments: requiredDocs,
    });
    alert('Workspace settings saved');
  }

  function onBackendChange(e) {
    const { name, value } = e.target;
    setBackendSettings((current) => ({ ...current, [name]: value }));
  }

  async function saveBackendSettings() {
    try {
      setSavingBackendSettings(true);
      setBackendError('');
      const updated = await adminUpdateWebsiteSettings(backendSettings);
      const leaseEmail = updated?.leaseEmail || {};
      const paymentEmail = updated?.paymentEmail || {};
      const bankDetails = updated?.bankDetails || {};
      setBackendSettings({
        leaseEmailSubject: leaseEmail.subject || '',
        leaseEmailBody: leaseEmail.body || '',
        leaseEmailFromName: leaseEmail.fromName || '',
        leaseEmailFromAddress: leaseEmail.fromAddress || '',
        leaseEmailCc: leaseEmail.cc || '',
        leaseEmailBcc: leaseEmail.bcc || '',
        paymentLink: bankDetails.paymentLink || '',
        bankName: bankDetails.bankName || '',
        bankAccountName: bankDetails.accountName || '',
        bankAccountNumber: bankDetails.accountNumber || '',
        bankBranchCode: bankDetails.branchCode || '',
        bankReferenceFormat: bankDetails.referenceFormat || '',
        paymentEmailSubject: paymentEmail.subject || '',
        paymentEmailBody: paymentEmail.body || '',
        paymentEmailFromName: paymentEmail.fromName || '',
        paymentEmailFromAddress: paymentEmail.fromAddress || '',
        paymentEmailCc: paymentEmail.cc || '',
        paymentEmailBcc: paymentEmail.bcc || '',
      });
      setLeaseMergeFields(Array.isArray(leaseEmail.mergeFields) ? leaseEmail.mergeFields : []);
      setPaymentMergeFields(Array.isArray(paymentEmail.mergeFields) ? paymentEmail.mergeFields : []);
      alert('Email and bank defaults saved');
    } catch (e) {
      setBackendError(e?.message || 'Failed to save email and payment settings.');
      alert(e?.message || 'Failed to save email and payment settings.');
    } finally {
      setSavingBackendSettings(false);
    }
  }

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-gray-600">Configure workspace controls, bank details, and outgoing email defaults.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.85fr_1.15fr]">
        <div className="bg-white rounded-xl shadow p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Workspace Settings</h2>
            <p className="text-sm text-gray-600 mt-1">These controls stay local to the admin workspace.</p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Manual Creation</div>
              <div className="text-sm text-gray-600">Allow team to create applicants manually from the admin UI.</div>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only" checked={manualEnabled} onChange={(e) => setManualEnabled(e.target.checked)} />
              <div className={`w-12 h-6 rounded-full transition ${manualEnabled ? 'bg-orange-500' : 'bg-gray-300'}`}>
                <div className={`h-6 w-6 bg-white rounded-full transform transition ${manualEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
              </div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Apply from Properties page</div>
              <div className="text-sm text-gray-600">Enable or disable self-service applications from listing pages.</div>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only" checked={allowApply} onChange={(e) => setAllowApply(e.target.checked)} />
              <div className={`w-12 h-6 rounded-full transition ${allowApply ? 'bg-orange-500' : 'bg-gray-300'}`}>
                <div className={`h-6 w-6 bg-white rounded-full transform transition ${allowApply ? 'translate-x-6' : 'translate-x-0'}`} />
              </div>
            </label>
          </div>

          <div>
            <div className="font-medium mb-2">Required Documents</div>
            <div className="flex gap-2 mb-3">
              <input className="flex-1 border rounded px-3 py-2" placeholder="Add document name" value={newDoc} onChange={(e) => setNewDoc(e.target.value)} />
              <button className="px-3 py-2 border rounded" onClick={addDoc}>Add</button>
            </div>
            <div className="space-y-2">
              {requiredDocs.map((doc) => (
                <div key={doc} className="flex items-center justify-between border rounded px-3 py-2">
                  <span>{doc}</span>
                  <button className="text-red-600 text-sm" onClick={() => removeDoc(doc)}>Remove</button>
                </div>
              ))}
              {requiredDocs.length === 0 && <div className="text-sm text-gray-600">No documents configured.</div>}
            </div>
          </div>

          <div className="flex justify-end">
            <button className="px-4 py-2 bg-orange-600 text-white rounded" onClick={saveWorkspaceSettings}>Save Workspace Settings</button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Payment & Email Defaults</h2>
            <p className="text-sm text-gray-600 mt-1">These settings are stored in the backend and used by lease emails and payment requests.</p>
          </div>

          {loadingBackendSettings ? (
            <div className="text-sm text-gray-500">Loading email and payment settings…</div>
          ) : (
            <>
              {backendError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {backendError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">Bank Details</h3>
                  <p className="text-sm text-gray-600 mt-1">These values are merged into payment request emails.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="border rounded px-3 py-2 sm:col-span-2" placeholder="Payment Link (optional)" name="paymentLink" value={backendSettings.paymentLink} onChange={onBackendChange} />
                  <input className="border rounded px-3 py-2" placeholder="Bank Name" name="bankName" value={backendSettings.bankName} onChange={onBackendChange} />
                  <input className="border rounded px-3 py-2" placeholder="Account Name" name="bankAccountName" value={backendSettings.bankAccountName} onChange={onBackendChange} />
                  <input className="border rounded px-3 py-2" placeholder="Account Number" name="bankAccountNumber" value={backendSettings.bankAccountNumber} onChange={onBackendChange} />
                  <input className="border rounded px-3 py-2" placeholder="Branch Code" name="bankBranchCode" value={backendSettings.bankBranchCode} onChange={onBackendChange} />
                  <input className="border rounded px-3 py-2 sm:col-span-2" placeholder="Reference Format e.g. PROPERTY-UNIT-NAME" name="bankReferenceFormat" value={backendSettings.bankReferenceFormat} onChange={onBackendChange} />
                </div>
              </div>

              <div className="space-y-4 border-t pt-6">
                <div>
                  <h3 className="font-medium text-gray-900">Lease Email</h3>
                  <p className="text-sm text-gray-600 mt-1">Used for auto-send on approval and manual lease resend.</p>
                </div>

                <input className="w-full border rounded px-3 py-2" name="leaseEmailSubject" value={backendSettings.leaseEmailSubject} onChange={onBackendChange} placeholder="Lease email subject" />
                <textarea className="w-full border rounded px-3 py-2 font-mono text-sm" name="leaseEmailBody" value={backendSettings.leaseEmailBody} onChange={onBackendChange} rows={10} />

                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="border rounded px-3 py-2" name="leaseEmailFromName" value={backendSettings.leaseEmailFromName} onChange={onBackendChange} placeholder="From Name" />
                  <input className="border rounded px-3 py-2" name="leaseEmailFromAddress" value={backendSettings.leaseEmailFromAddress} onChange={onBackendChange} placeholder="From Address" />
                  <input className="border rounded px-3 py-2" name="leaseEmailCc" value={backendSettings.leaseEmailCc} onChange={onBackendChange} placeholder="CC" />
                  <input className="border rounded px-3 py-2" name="leaseEmailBcc" value={backendSettings.leaseEmailBcc} onChange={onBackendChange} placeholder="BCC" />
                </div>

                <div className="rounded-lg border bg-gray-50 p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">Lease merge fields</div>
                  <div className="flex flex-wrap gap-2">
                    {leaseMergeFields.map((field) => (
                      <span key={field} className="rounded-full border bg-white px-2 py-1 text-xs text-gray-700">{`{{${field}}}`}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t pt-6">
                <div>
                  <h3 className="font-medium text-gray-900">Payment Request Email</h3>
                  <p className="text-sm text-gray-600 mt-1">Sent from the tenant screens when you click Request Payment.</p>
                </div>

                <input className="w-full border rounded px-3 py-2" name="paymentEmailSubject" value={backendSettings.paymentEmailSubject} onChange={onBackendChange} placeholder="Payment request subject" />
                <textarea className="w-full border rounded px-3 py-2 font-mono text-sm" name="paymentEmailBody" value={backendSettings.paymentEmailBody} onChange={onBackendChange} rows={12} />

                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="border rounded px-3 py-2" name="paymentEmailFromName" value={backendSettings.paymentEmailFromName} onChange={onBackendChange} placeholder="From Name" />
                  <input className="border rounded px-3 py-2" name="paymentEmailFromAddress" value={backendSettings.paymentEmailFromAddress} onChange={onBackendChange} placeholder="From Address" />
                  <input className="border rounded px-3 py-2" name="paymentEmailCc" value={backendSettings.paymentEmailCc} onChange={onBackendChange} placeholder="CC" />
                  <input className="border rounded px-3 py-2" name="paymentEmailBcc" value={backendSettings.paymentEmailBcc} onChange={onBackendChange} placeholder="BCC" />
                </div>

                <div className="rounded-lg border bg-gray-50 p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">Payment merge fields</div>
                  <div className="flex flex-wrap gap-2">
                    {paymentMergeFields.map((field) => (
                      <span key={field} className="rounded-full border bg-white px-2 py-1 text-xs text-gray-700">{`{{${field}}}`}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button className="px-4 py-2 bg-orange-600 text-white rounded disabled:opacity-50" onClick={saveBackendSettings} disabled={savingBackendSettings}>
                  {savingBackendSettings ? 'Saving…' : 'Save Payment & Email Defaults'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
